import React, { useState, useEffect, useContext } from 'react';
import { apiGet, apiPost, apiPut, apiDelete, apiUpload, API_ENDPOINTS, getFullImageUrl } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import toast, { Toaster } from 'react-hot-toast';
import { useNetworkStatus } from '../hooks/useNetworkStatus';
import { queueInventoryAdjustment, getCachedProducts, updateCachedProductStock, getPendingInventoryAdjustments } from '../services/offlineStore';
import ImageManagerModal from './ImageManagerModal';
import { Upload, ArrowUp, ArrowDown, Star, Trash2, Image as ImageIcon, Plus, Link as LinkIcon, Loader2 } from 'lucide-react';

const Inventory = () => {
  const { user, hasRole } = useAuth();
  const { isOnline } = useNetworkStatus();
  const [activeTab, setActiveTab] = useState('products');
  const [products, setProducts] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [barcodeInput, setBarcodeInput] = useState('');
  const [scanningMode, setScanningMode] = useState(false);
  const [scannedProduct, setScannedProduct] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    sku: '',
    price: '',
    category: '',
    stock_level: '',
    stock_by_size: { S: 0, M: 0, L: 0, XL: 0 },
    duration: '',
    max_duration: '',
    images: [],
    image_url: ''
  });
  const [useSizeStock, setUseSizeStock] = useState(false);
  const [formErrors, setFormErrors] = useState({});

  // Image management modal state
  const [imageModalItem, setImageModalItem] = useState(null);
  const [imageModalType, setImageModalType] = useState('product');
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [formImageTab, setFormImageTab] = useState('upload');
  const [formImageUrlInput, setFormImageUrlInput] = useState('');
  const [formImageUploading, setFormImageUploading] = useState(false);

  // Movement history state
  const [movements, setMovements] = useState([]);
  const [movementsLoading, setMovementsLoading] = useState(false);
  const [movementFilter, setMovementFilter] = useState('');
  const [movementTypeFilter, setMovementTypeFilter] = useState('');

  // Channel selector state
  const [formChannels, setFormChannels] = useState([]);
  const AVAILABLE_CHANNELS = ['walk-in', 'pos', 'online', 'booking'];
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isAdmin = hasRole('admin');
  const isManager = hasRole('manager');
  const canManageInventory = isAdmin || isManager;
  const canEditOrDelete = isAdmin;


  useEffect(() => {
    fetchProducts();
    fetchServices();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await apiGet(API_ENDPOINTS.INVENTORY);
      setProducts(Array.isArray(response) ? response : (response.data || []));
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const fetchServices = async () => {
    try {
      const response = await apiGet(API_ENDPOINTS.SERVICES);
      setServices(Array.isArray(response) ? response : (response.data || []));
    } catch (error) {
      console.error('Error fetching services:', error);
    } finally {
      if (activeTab === 'services') setLoading(false);
    }
  };

  const fetchMovements = async () => {
    setMovementsLoading(true);
    try {
      let url = `${API_ENDPOINTS.INVENTORY_MOVEMENTS}?limit=50`;
      if (movementTypeFilter) url += `&movement_type=${movementTypeFilter}`;
      const response = await apiGet(url);
      setMovements(response.movements || response || []);
    } catch (error) {
      console.error('Error fetching movements:', error);
      toast.error('Failed to load movement history');
    } finally {
      setMovementsLoading(false);
    }
  };

  useEffect(() => {
    if (products.length >= 0 && services.length >= 0) {
      setLoading(false);
    }
  }, [products, services]);

  // Derived filtered data
  const filteredProducts = products.filter(p => 
    p.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.category?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.sku?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredServices = services.filter(s => 
    s.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.category?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const validateForm = () => {
    const errors = {};

    // Common Price validation
    if (!formData.price || parseFloat(formData.price) <= 0) {
      errors.price = 'Price must be greater than 0';
    }

    if (activeTab === 'products') {
      // Stock validation
      if (!useSizeStock) {
        if (formData.stock_level === '' || parseInt(formData.stock_level) < 0) {
          errors.stock_level = 'Stock level cannot be negative';
        }
      } else {
        // Validate size stocks
        const sizes = ['S', 'M', 'L', 'XL'];
        sizes.forEach(size => {
          if (parseInt(formData.stock_by_size[size] || 0) < 0) {
            errors[`stock_${size}`] = `${size} stock cannot be negative`;
          }
        });
      }
      if (!formData.name.trim()) errors.name = 'Product name is required';
      if (!formData.sku.trim()) errors.sku = 'SKU is required';
      if (!formData.category.trim()) errors.category = 'Category is required';
    } else {
      // Service validation
      if (!formData.duration || parseInt(formData.duration) <= 0) {
        errors.duration = 'Duration is required';
      }
      if (!formData.name.trim()) errors.name = 'Service name is required';
      if (!formData.category.trim()) errors.category = 'Category is required';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // Clear error when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }

    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSizeStockChange = (size, value) => {
    setFormData(prev => ({
      ...prev,
      stock_by_size: {
        ...prev.stock_by_size,
        [size]: parseInt(value) || 0
      }
    }));
  };

  // Form image manipulation handlers
  const handleFormFileUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setFormImageUploading(true);
    try {
      const currentList = [...(formData.images || [])];
      for (const file of files) {
        const uploadRes = await apiUpload(API_ENDPOINTS.UPLOAD_IMAGE, file);
        const fileUrl = uploadRes?.data?.url || uploadRes?.url;
        if (fileUrl) {
          const nextRank = currentList.length + 1;
          currentList.push({
            id: `temp-${Date.now()}-${Math.random().toString(36).substr(2, 8)}`,
            image_url: fileUrl,
            ranking: nextRank,
            caption: file.name.replace(/\.[^/.]+$/, ''),
            is_primary: currentList.length === 0
          });
        }
      }
      setFormData(prev => ({
        ...prev,
        images: currentList,
        image_url: currentList[0]?.image_url || ''
      }));
      toast.success(`${files.length} image(s) uploaded!`);
    } catch (err) {
      console.error('Upload error in form:', err);
      toast.error('Failed to upload image file');
    } finally {
      setFormImageUploading(false);
      if (e.target) e.target.value = '';
    }
  };

  const handleFormAddUrl = (e) => {
    e.preventDefault();
    if (!formImageUrlInput.trim()) return;
    const currentList = [...(formData.images || [])];
    const nextRank = currentList.length + 1;
    currentList.push({
      id: `temp-${Date.now()}`,
      image_url: formImageUrlInput.trim(),
      ranking: nextRank,
      caption: '',
      is_primary: currentList.length === 0
    });
    setFormData(prev => ({
      ...prev,
      images: currentList,
      image_url: currentList[0]?.image_url || ''
    }));
    setFormImageUrlInput('');
    toast.success('Image added to gallery!');
  };

  const handleFormMoveImageUp = (index) => {
    if (index <= 0) return;
    const currentList = [...(formData.images || [])];
    const item = currentList[index];
    currentList[index] = currentList[index - 1];
    currentList[index - 1] = item;
    const reordered = currentList.map((img, i) => ({
      ...img,
      ranking: i + 1,
      is_primary: i === 0
    }));
    setFormData(prev => ({
      ...prev,
      images: reordered,
      image_url: reordered[0]?.image_url || ''
    }));
  };

  const handleFormMoveImageDown = (index) => {
    const currentList = formData.images || [];
    if (index >= currentList.length - 1) return;
    const list = [...currentList];
    const item = list[index];
    list[index] = list[index + 1];
    list[index + 1] = item;
    const reordered = list.map((img, i) => ({
      ...img,
      ranking: i + 1,
      is_primary: i === 0
    }));
    setFormData(prev => ({
      ...prev,
      images: reordered,
      image_url: reordered[0]?.image_url || ''
    }));
  };

  const handleFormSetCover = (index) => {
    if (index <= 0) return;
    const list = [...(formData.images || [])];
    const [target] = list.splice(index, 1);
    list.unshift(target);
    const reordered = list.map((img, i) => ({
      ...img,
      ranking: i + 1,
      is_primary: i === 0
    }));
    setFormData(prev => ({
      ...prev,
      images: reordered,
      image_url: reordered[0]?.image_url || ''
    }));
  };

  const handleFormRemoveImage = (index) => {
    const list = (formData.images || []).filter((_, i) => i !== index);
    const reordered = list.map((img, i) => ({
      ...img,
      ranking: i + 1,
      is_primary: i === 0
    }));
    setFormData(prev => ({
      ...prev,
      images: reordered,
      image_url: reordered[0]?.image_url || ''
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsSubmitting(true);
    try {
      if (activeTab === 'products') {
        const submitData = {
          ...formData,
          price: parseFloat(formData.price),
          stock_level: useSizeStock
            ? Object.values(formData.stock_by_size).reduce((a, b) => a + b, 0)
            : parseInt(formData.stock_level),
          stock_by_size: useSizeStock ? formData.stock_by_size : null,
          images: formData.images || [],
          image_url: formData.images?.[0]?.image_url || formData.image_url || null
        };
        if (editingItem) {
          await apiPut(`${API_ENDPOINTS.INVENTORY}/${editingItem.id}`, submitData);
        } else {
          await apiPost(API_ENDPOINTS.INVENTORY, submitData);
        }
        fetchProducts();
      } else {
        const serviceData = {
          name: formData.name,
          description: formData.description,
          price: parseFloat(formData.price),
          duration: parseInt(formData.duration),
          max_duration: formData.max_duration ? parseInt(formData.max_duration) : null,
          category: formData.category,
          images: formData.images || [],
          image_url: formData.images?.[0]?.image_url || formData.image_url || null
        };
        if (editingItem) {
          await apiPut(`${API_ENDPOINTS.SERVICES}/${editingItem.id}`, serviceData);
        } else {
          await apiPost(API_ENDPOINTS.SERVICES, serviceData);
        }
        fetchServices();
      }

      resetForm();
      toast.success(`${activeTab === 'products' ? 'Product' : 'Service'} saved successfully!`);
    } catch (error) {
      console.error('Error saving:', error);
      toast.error(error.error?.message || error.message || 'Error saving. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    const itemImages = item.images && Array.isArray(item.images) && item.images.length > 0
      ? [...item.images].sort((a, b) => (a.ranking || 0) - (b.ranking || 0))
      : (item.image_url ? [{ id: 'init', image_url: item.image_url, ranking: 1, is_primary: true }] : []);

    if (activeTab === 'products') {
      const hasSizeStock = item.stock_by_size && Object.keys(item.stock_by_size).length > 0;
      setUseSizeStock(hasSizeStock);
      setFormChannels(item.channels || []);
      setFormData({
        ...formData,
        name: item.name,
        description: item.description || '',
        sku: item.sku,
        price: item.price,
        category: item.category,
        stock_level: item.stock_level,
        stock_by_size: hasSizeStock ? item.stock_by_size : { S: 0, M: 0, L: 0, XL: 0 },
        images: itemImages,
        image_url: item.image_url || itemImages[0]?.image_url || ''
      });
    } else {
      setFormData({
        ...formData,
        name: item.name,
        description: item.description || '',
        price: item.price,
        category: item.category,
        duration: item.duration,
        max_duration: item.max_duration || '',
        images: itemImages,
        image_url: item.image_url || itemImages[0]?.image_url || ''
      });
    }
    setShowAddForm(true);
  };

  const handleDelete = async (item) => {
    const type = activeTab === 'products' ? 'product' : 'service';
    if (!window.confirm(`Are you sure you want to delete this ${type} "${item.name}"?`)) return;
    try {
      const endpoint = activeTab === 'products' ? API_ENDPOINTS.INVENTORY : API_ENDPOINTS.SERVICES;
      await apiDelete(`${endpoint}/${item.id}`);
      activeTab === 'products' ? fetchProducts() : fetchServices();
      toast.success(`${type.charAt(0).toUpperCase() + type.slice(1)} deleted successfully!`);
    } catch (error) {
      console.error('Error deleting:', error);
      toast.error(error?.error?.message || 'Error deleting. Please try again.');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      sku: '',
      price: '',
      category: '',
      stock_level: '',
      stock_by_size: { S: 0, M: 0, L: 0, XL: 0 },
      duration: '',
      max_duration: '',
      images: [],
      image_url: ''
    });
    setUseSizeStock(false);
    setEditingItem(null);
    setShowAddForm(false);
    setFormErrors({});
    setFormChannels([]);
    setFormImageUrlInput('');
    setFormImageTab('upload');
  };

  const handleOpenImageModal = (item, type = 'product') => {
    setImageModalItem(item);
    setImageModalType(type);
    setIsImageModalOpen(true);
  };

  const handleCloseImageModal = () => {
    setImageModalItem(null);
    setIsImageModalOpen(false);
  };

  const getStockStatus = (stockLevel) => {
    if (stockLevel === 0) return { status: 'Out of Stock', color: 'text-red-600 bg-red-100' };
    if (stockLevel < 5) return { status: 'Low Stock', color: 'text-yellow-600 bg-yellow-100' };
    return { status: 'In Stock', color: 'text-green-600 bg-green-100' };
  };

  const handleBarcodeInput = (e) => {
    const barcode = e.target.value;
    setBarcodeInput(barcode);

    // Auto-search when barcode is entered (typically 8-13 digits)
    if (barcode.length >= 8 && /^\d+$/.test(barcode)) {
      searchProductByBarcode(barcode);
    }
  };

  const searchProductByBarcode = async (barcode) => {
    try {
      const product = await apiGet(API_ENDPOINTS.INVENTORY_BARCODE(barcode));
      setScannedProduct(product);
      if (scanningMode) {
        // Auto-fill form for quick stock adjustment
        const hasSizeStock = product.stock_by_size && Object.keys(product.stock_by_size).length > 0;
        setUseSizeStock(hasSizeStock);
        setFormData({
          name: product.name,
          description: product.description,
          sku: product.sku,
          price: product.price,
          category: product.category,
          stock_level: product.stock_level,
          stock_by_size: hasSizeStock ? product.stock_by_size : { S: 0, M: 0, L: 0, XL: 0 }
        });
        setEditingItem(product);
        setShowAddForm(true);
      }
    } catch (error) {
      console.error('Error searching product:', error);
      setScannedProduct(null);
      toast.error('Product not found with this SKU');
    }
  };

  const toggleScanningMode = () => {
    setScanningMode(!scanningMode);
    setBarcodeInput('');
    setScannedProduct(null);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64" role="status" aria-label="Loading inventory">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        <span className="sr-only">Loading inventory...</span>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-full">
      <Toaster position="top-right" />
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Inventory & Services</h1>
          <p className="text-gray-600">Manage your product stock and service offerings</p>
        </div>
        <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3">
          <button
            onClick={toggleScanningMode}
            className={`px-4 py-2 rounded-md focus:outline-none focus:ring-2 ${scanningMode
              ? 'bg-green-500 text-white hover:bg-green-600 focus:ring-green-500'
              : 'bg-gray-500 text-white hover:bg-gray-600 focus:ring-gray-500'
              }`}
            aria-label={scanningMode ? 'Disable scanning mode' : 'Enable scanning mode'}
          >
            {scanningMode ? '📱 Scanning ON' : '📱 Enable Scanner'}
          </button>
          {canManageInventory && (
            <button
              onClick={() => {
                resetForm();
                setShowAddForm(true);
              }}
              className="bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600 transition"
            >
              Add {activeTab === 'products' ? 'Product' : 'Service'}
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-200 mb-6 sticky top-0 bg-white z-10">
        <button
          onClick={() => setActiveTab('products')}
          className={`py-2 px-6 font-medium text-sm transition-colors duration-200 border-b-2 ${activeTab === 'products'
            ? 'border-blue-500 text-blue-600'
            : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
        >
          📦 Products
        </button>
        <button
          onClick={() => setActiveTab('services')}
          className={`py-2 px-6 font-medium text-sm transition-colors duration-200 border-b-2 ${activeTab === 'services'
            ? 'border-blue-500 text-blue-600'
            : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
        >
          💇‍♀️ Services
        </button>
        <button
          onClick={() => { setActiveTab('movements'); fetchMovements(); }}
          className={`py-2 px-6 font-medium text-sm transition-colors duration-200 border-b-2 ${activeTab === 'movements'
            ? 'border-blue-500 text-blue-600'
            : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
        >
          📋 Movements
        </button>
        <div className="ml-auto w-1/3 min-w-[200px] pb-2">
          <input
            type="text"
            placeholder={`Search ${activeTab}...`}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-4 py-2 border rounded-full text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none shadow-sm"
          />
        </div>
      </div>

      {/* Content based on active tab */}
      {activeTab === 'products' ? (
        <>
          {/* Barcode Scanner Section */}
          <div className="bg-white p-4 rounded-lg shadow mb-6">
            <h3 className="text-lg font-semibold text-gray-700 mb-3">
              🔍 SKU Scanner {scanningMode && <span className="text-green-600">(Active)</span>}
            </h3>
            <div className="flex flex-col sm:flex-row sm:space-x-3 space-y-3 sm:space-y-0 mb-4">
              <input
                type="text"
                placeholder="Scan or enter SKU..."
                value={barcodeInput}
                onChange={handleBarcodeInput}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                aria-label="SKU input field"
                autoFocus={scanningMode}
              />
              <button
                onClick={() => searchProductByBarcode(barcodeInput)}
                disabled={!barcodeInput.trim()}
                className="px-6 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label="Search product by SKU"
              >
                Search
              </button>
            </div>

            {/* Scanned Product Display */}
            {scannedProduct && (
              <div className="p-3 bg-green-50 border border-green-200 rounded-md">
                <h4 className="font-semibold text-green-800 mb-2">Product Found:</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm mb-3">
                  <div><strong>Name:</strong> {scannedProduct.name}</div>
                  <div><strong>SKU:</strong> {scannedProduct.sku}</div>
                  <div><strong>Stock:</strong> {scannedProduct.stock_level}</div>
                  <div><strong>Price:</strong> ₦{parseFloat(scannedProduct.price || 0).toFixed(2)}</div>
                </div>
                <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2">
                  {canEditOrDelete && (
                    <button
                      onClick={() => handleEdit(scannedProduct)}
                      className="px-3 py-1 bg-blue-500 text-white text-sm rounded hover:bg-blue-600"
                      aria-label="Edit product details"
                    >
                      Edit Product
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Products Table */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Product</th>
                    <th className="hidden md:table-cell px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">SKU</th>
                    <th className="hidden sm:table-cell px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Price</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Stock</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredProducts.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="px-6 py-4 text-center text-gray-500">
                        {searchTerm ? 'No matches found for your search' : 'No products found'}
                      </td>
                    </tr>
                  ) : (
                    filteredProducts.map((product) => {
                      const stockStatus = getStockStatus(product.stock_level);
                      return (
                        <tr key={product.id} className="hover:bg-gray-50/80 transition-colors">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center space-x-3">
                              <button
                                type="button"
                                onClick={() => handleOpenImageModal(product, 'product')}
                                className="w-10 h-10 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0 border border-gray-200 hover:ring-2 hover:ring-blue-400 transition cursor-pointer relative group flex items-center justify-center"
                                title="Click to view & manage images"
                              >
                                {product.image_url || product.images?.[0]?.image_url ? (
                                  <img
                                    src={getFullImageUrl(product.image_url || product.images?.[0]?.image_url)}
                                    alt={product.name}
                                    className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                                  />
                                ) : (
                                  <ImageIcon className="w-5 h-5 text-gray-400 group-hover:text-blue-500 transition-colors" />
                                )}
                              </button>
                              <div>
                                <div className="text-sm font-medium text-gray-900">{product.name}</div>
                                {product.images?.length > 1 && (
                                  <span className="text-[10px] text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded-full font-medium">
                                    +{product.images.length - 1} more
                                  </span>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="hidden md:table-cell px-6 py-4 whitespace-nowrap text-sm text-gray-500">{product.sku}</td>
                          <td className="hidden sm:table-cell px-6 py-4 whitespace-nowrap text-sm text-gray-500">{product.category}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">₦{parseFloat(product.price).toLocaleString()}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {product.stock_level}
                            {product.stock_by_size && Object.keys(product.stock_by_size).length > 0 && (
                              <div className="text-xs text-gray-400">
                                {Object.entries(product.stock_by_size)
                                  .filter(([_, q]) => q > 0)
                                  .map(([s, q]) => `${s}:${q}`)
                                  .join(' ')}
                              </div>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 py-1 text-xs rounded-full ${stockStatus.color}`}>{stockStatus.status}</span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                            <button
                              onClick={() => handleOpenImageModal(product, 'product')}
                              className="text-purple-600 hover:text-purple-900 inline-flex items-center gap-1 text-xs font-semibold bg-purple-50 hover:bg-purple-100 px-2.5 py-1 rounded transition"
                              title="Manage & rank images"
                            >
                              📷 Images {product.images?.length ? `(${product.images.length})` : ''}
                            </button>
                            {canEditOrDelete && (
                              <>
                                <button onClick={() => handleEdit(product)} className="text-blue-600 hover:text-blue-900">Edit</button>
                                <button onClick={() => handleDelete(product)} className="text-red-600 hover:text-red-900">Delete</button>
                              </>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      ) : (
        /* Services Table */
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Service Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Price</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Duration</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredServices.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="px-6 py-4 text-center text-gray-500">
                      {searchTerm ? 'No matches found for your search' : 'No services found'}
                    </td>
                  </tr>
                ) : (
                  filteredServices.map((service) => (
                    <tr key={service.id} className="hover:bg-gray-50/80 transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-center space-x-3">
                          <button
                            type="button"
                            onClick={() => handleOpenImageModal(service, 'service')}
                            className="w-10 h-10 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0 border border-gray-200 hover:ring-2 hover:ring-purple-400 transition cursor-pointer relative group flex items-center justify-center"
                            title="Click to view & manage images"
                          >
                            {service.image_url || service.images?.[0]?.image_url ? (
                              <img
                                src={getFullImageUrl(service.image_url || service.images?.[0]?.image_url)}
                                alt={service.name}
                                className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                              />
                            ) : (
                              <ImageIcon className="w-5 h-5 text-gray-400 group-hover:text-purple-500 transition-colors" />
                            )}
                          </button>
                          <div>
                            <div className="text-sm font-medium text-gray-900">{service.name}</div>
                            <div className="text-xs text-gray-500 truncate max-w-xs">{service.description}</div>
                            {service.images?.length > 1 && (
                              <span className="text-[10px] text-purple-600 bg-purple-50 px-1.5 py-0.5 rounded-full font-medium">
                                +{service.images.length - 1} more
                              </span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{service.category}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">₦{parseFloat(service.price).toLocaleString()}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{service.duration} - {service.max_duration || service.duration} mins</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                        <button
                          onClick={() => handleOpenImageModal(service, 'service')}
                          className="text-purple-600 hover:text-purple-900 inline-flex items-center gap-1 text-xs font-semibold bg-purple-50 hover:bg-purple-100 px-2.5 py-1 rounded transition"
                          title="Manage & rank images"
                        >
                          📷 Images {service.images?.length ? `(${service.images.length})` : ''}
                        </button>
                        {canEditOrDelete && (
                          <>
                            <button onClick={() => handleEdit(service)} className="text-blue-600 hover:text-blue-900">Edit</button>
                            <button onClick={() => handleDelete(service)} className="text-red-600 hover:text-red-900">Delete</button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Movements History Panel */}
      {activeTab === 'movements' && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="p-4 border-b border-gray-200 flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-700">Inventory Movement History</h3>
            <div className="flex gap-2 w-full sm:w-auto">
              <input
                type="text"
                placeholder="Search by product..."
                value={movementFilter}
                onChange={(e) => setMovementFilter(e.target.value)}
                className="flex-1 sm:w-48 px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <select
                value={movementTypeFilter}
                onChange={(e) => { setMovementTypeFilter(e.target.value); }}
                className="px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Types</option>
                <option value="sale">Sale</option>
                <option value="restock">Restock</option>
                <option value="adjustment">Adjustment</option>
                <option value="return">Return</option>
              </select>
              <button
                onClick={fetchMovements}
                className="px-3 py-1.5 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700 transition"
              >
                Refresh
              </button>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Product</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Qty</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Size</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Note</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">By</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {movementsLoading ? (
                  <tr><td colSpan={7} className="px-4 py-8 text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
                  </td></tr>
                ) : movements.length === 0 ? (
                  <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-500">No movement records found</td></tr>
                ) : movements
                  .filter(m => {
                    if (!movementFilter) return true;
                    const q = movementFilter.toLowerCase();
                    return (m.product_name || '').toLowerCase().includes(q) || (m.sku || '').toLowerCase().includes(q);
                  })
                  .map(m => (
                    <tr key={m.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                        {new Date(m.created_at).toLocaleDateString()} {new Date(m.created_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{m.product_name || 'Unknown'}</div>
                        <div className="text-xs text-gray-400">{m.sku || ''}</div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${
                          m.movement_type === 'sale' ? 'bg-red-100 text-red-700' :
                          m.movement_type === 'restock' ? 'bg-green-100 text-green-700' :
                          m.movement_type === 'adjustment' ? 'bg-yellow-100 text-yellow-700' :
                          m.movement_type === 'return' ? 'bg-blue-100 text-blue-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {m.movement_type}
                        </span>
                      </td>
                      <td className={`px-4 py-3 whitespace-nowrap text-sm font-medium ${
                        m.quantity < 0 ? 'text-red-600' : 'text-green-600'
                      }`}>
                        {m.quantity > 0 ? '+' : ''}{m.quantity}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">{m.size || '-'}</td>
                      <td className="px-4 py-3 text-sm text-gray-500 max-w-xs truncate">{m.note || '-'}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">{m.created_by_name || '-'}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Add/Edit Form Modal */}
      {showAddForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" role="dialog" aria-modal="true">
          <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold">
                {editingItem ? 'Edit' : 'Add'} {activeTab === 'products' ? 'Product' : 'Service'}
              </h3>
              <button onClick={resetForm} className="text-gray-400 hover:text-gray-600">✕</button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-md mt-1 ${formErrors.name ? 'border-red-500' : 'border-gray-300'}`}
                />
                {formErrors.name && <p className="text-xs text-red-500 mt-1">{formErrors.name}</p>}
              </div>

              {activeTab === 'products' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">SKU *</label>
                  <input
                    type="text"
                    name="sku"
                    value={formData.sku}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 border rounded-md mt-1 ${formErrors.sku ? 'border-red-500' : 'border-gray-300'}`}
                  />
                  {formErrors.sku && <p className="text-xs text-red-500 mt-1">{formErrors.sku}</p>}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700">Category *</label>
                <input
                  type="text"
                  name="category"
                  value={formData.category}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-md mt-1 ${formErrors.category ? 'border-red-500' : 'border-gray-300'}`}
                />
                {formErrors.category && <p className="text-xs text-red-500 mt-1">{formErrors.category}</p>}
              </div>

              {activeTab === 'products' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Sales Channels</label>
                  <div className="flex flex-wrap gap-2">
                    {AVAILABLE_CHANNELS.map(ch => (
                      <button
                        key={ch}
                        type="button"
                        onClick={() => {
                          setFormChannels(prev =>
                            prev.includes(ch) ? prev.filter(c => c !== ch) : [...prev, ch]
                          );
                        }}
                        className={`px-3 py-1.5 text-xs font-medium rounded-full border transition ${
                          formChannels.includes(ch)
                            ? 'bg-blue-100 border-blue-400 text-blue-700'
                            : 'bg-gray-50 border-gray-300 text-gray-600 hover:bg-gray-100'
                        }`}
                      >
                        {ch === 'walk-in' ? '🏪 Walk-in' :
                         ch === 'pos' ? '💳 POS' :
                         ch === 'online' ? '🌐 Online' :
                         ch === 'booking' ? '📅 Booking' : ch}
                      </button>
                    ))}
                  </div>
                  <input type="hidden" name="channels" value={JSON.stringify(formChannels)} />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700">Price (₦) *</label>
                <input
                  type="number"
                  name="price"
                  value={formData.price}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-md mt-1 ${formErrors.price ? 'border-red-500' : 'border-gray-300'}`}
                />
                {formErrors.price && <p className="text-xs text-red-500 mt-1">{formErrors.price}</p>}
              </div>

              {activeTab === 'services' ? (
                <div className="flex gap-4">
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700">Duration (min) *</label>
                    <input
                      type="number"
                      name="duration"
                      value={formData.duration}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 border rounded-md mt-1 ${formErrors.duration ? 'border-red-500' : 'border-gray-300'}`}
                    />
                    {formErrors.duration && <p className="text-xs text-red-500 mt-1">{formErrors.duration}</p>}
                  </div>
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700">Max Duration</label>
                    <input
                      type="number"
                      name="max_duration"
                      value={formData.max_duration}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md mt-1"
                    />
                  </div>
                </div>
              ) : (
                /* Product Stock Section */
                <div>
                  <div className="flex items-center mb-2">
                    <input
                      type="checkbox"
                      id="useSize"
                      checked={useSizeStock}
                      onChange={(e) => setUseSizeStock(e.target.checked)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="useSize" className="ml-2 block text-sm text-gray-900">Track by Size</label>
                  </div>

                  {useSizeStock ? (
                    <div className="grid grid-cols-4 gap-2">
                      {['S', 'M', 'L', 'XL'].map(size => (
                        <div key={size}>
                          <label className="text-xs font-semibold">{size}</label>
                          <input
                            type="number"
                            value={formData.stock_by_size[size]}
                            onChange={(e) => handleSizeStockChange(size, e.target.value)}
                            className="w-full px-2 py-1 text-sm border border-gray-300 rounded-md"
                          />
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Stock Level *</label>
                      <input
                        type="number"
                        name="stock_level"
                        value={formData.stock_level}
                        onChange={handleInputChange}
                        className={`w-full px-3 py-2 border rounded-md mt-1 ${formErrors.stock_level ? 'border-red-500' : 'border-gray-300'}`}
                      />
                    </div>
                  )}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700">Description</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md mt-1"
                />
              </div>

              {/* Images & Ranking Management Section */}
              <div className="pt-2 border-t border-gray-200">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <label className="block text-sm font-semibold text-gray-800">
                      📸 {activeTab === 'products' ? 'Product' : 'Service'} Images & Ranking
                    </label>
                    <p className="text-xs text-gray-500">
                      Rank #1 is the primary cover image. Use arrows to re-rank.
                    </p>
                  </div>
                  {editingItem && (
                    <button
                      type="button"
                      onClick={() => handleOpenImageModal(editingItem, activeTab === 'products' ? 'product' : 'service')}
                      className="text-xs text-purple-700 bg-purple-50 hover:bg-purple-100 border border-purple-200 px-2.5 py-1 rounded-md font-medium inline-flex items-center gap-1 transition"
                    >
                      ⚡ Open Image Studio
                    </button>
                  )}
                </div>

                {/* Upload or Add URL switcher */}
                <div className="bg-gray-50 p-3 rounded-lg border border-gray-200 mb-3">
                  <div className="flex gap-2 mb-2">
                    <button
                      type="button"
                      onClick={() => setFormImageTab('upload')}
                      className={`px-3 py-1 text-xs font-medium rounded-md transition ${
                        formImageTab === 'upload'
                          ? 'bg-blue-600 text-white shadow-sm'
                          : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-100'
                      }`}
                    >
                      <Upload className="w-3 h-3 inline mr-1" />
                      Upload File
                    </button>
                    <button
                      type="button"
                      onClick={() => setFormImageTab('url')}
                      className={`px-3 py-1 text-xs font-medium rounded-md transition ${
                        formImageTab === 'url'
                          ? 'bg-blue-600 text-white shadow-sm'
                          : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-100'
                      }`}
                    >
                      <LinkIcon className="w-3 h-3 inline mr-1" />
                      Add by URL
                    </button>
                  </div>

                  {formImageTab === 'upload' ? (
                    <div>
                      <label className="flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-lg p-3 bg-white hover:border-blue-400 cursor-pointer transition">
                        <Upload className="w-6 h-6 text-gray-400 mb-1" />
                        <span className="text-xs font-medium text-gray-700">
                          {formImageUploading ? 'Uploading image...' : 'Click to upload image file(s)'}
                        </span>
                        <span className="text-[10px] text-gray-400">PNG, JPG, WEBP, GIF up to 10MB</span>
                        <input
                          type="file"
                          multiple
                          accept="image/*"
                          onChange={handleFormFileUpload}
                          disabled={formImageUploading}
                          className="hidden"
                        />
                      </label>
                      {formImageUploading && (
                        <div className="flex items-center justify-center gap-2 mt-2 text-xs text-blue-600">
                          <Loader2 className="w-4 h-4 animate-spin" /> Uploading image...
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <input
                        type="url"
                        placeholder="https://example.com/image.jpg"
                        value={formImageUrlInput}
                        onChange={(e) => setFormImageUrlInput(e.target.value)}
                        className="flex-1 px-3 py-1.5 text-xs border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:outline-none"
                      />
                      <button
                        type="button"
                        onClick={handleFormAddUrl}
                        disabled={!formImageUrlInput.trim()}
                        className="px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded-md hover:bg-blue-700 disabled:opacity-50"
                      >
                        Add Image
                      </button>
                    </div>
                  )}
                </div>

                {/* Images List with Ranking Controls */}
                {formData.images && formData.images.length > 0 ? (
                  <div className="space-y-2 max-h-56 overflow-y-auto pr-1">
                    {formData.images.map((img, index) => {
                      const isCover = index === 0;
                      return (
                        <div
                          key={img.id || index}
                          className={`flex items-center justify-between p-2 rounded-lg border transition ${
                            isCover ? 'bg-amber-50/70 border-amber-300 ring-1 ring-amber-300/50' : 'bg-white border-gray-200'
                          }`}
                        >
                          <div className="flex items-center space-x-2.5 min-w-0">
                            {/* Rank badge */}
                            <span
                              className={`px-2 py-0.5 rounded text-[11px] font-bold flex items-center gap-0.5 ${
                                isCover
                                  ? 'bg-amber-500 text-white shadow-xs'
                                  : 'bg-gray-100 text-gray-700'
                              }`}
                              title={isCover ? 'Primary Cover Image (Rank 1)' : `Rank #${index + 1}`}
                            >
                              {isCover && <Star className="w-3 h-3 fill-current" />}
                              #{index + 1}
                            </span>

                            {/* Thumbnail */}
                            <img
                              src={getFullImageUrl(img.image_url)}
                              alt={img.caption || 'Product'}
                              className="w-10 h-10 object-cover rounded border border-gray-200 flex-shrink-0"
                            />

                            {/* Caption / URL info */}
                            <div className="min-w-0 flex-1">
                              <input
                                type="text"
                                placeholder="Caption / description"
                                value={img.caption || ''}
                                onChange={(e) => {
                                  const updated = [...formData.images];
                                  updated[index] = { ...updated[index], caption: e.target.value };
                                  setFormData(prev => ({ ...prev, images: updated }));
                                }}
                                className="text-xs px-2 py-0.5 border border-transparent hover:border-gray-200 focus:border-blue-400 rounded w-full bg-transparent focus:bg-white focus:outline-none"
                              />
                            </div>
                          </div>

                          {/* Ranking Actions */}
                          <div className="flex items-center space-x-1 flex-shrink-0 ml-2">
                            {!isCover && (
                              <button
                                type="button"
                                onClick={() => handleFormSetCover(index)}
                                className="px-1.5 py-0.5 text-[10px] bg-amber-100 text-amber-800 hover:bg-amber-200 rounded font-medium transition"
                                title="Set as primary cover (#1)"
                              >
                                Set Cover
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() => handleFormMoveImageUp(index)}
                              disabled={index === 0}
                              className="p-1 text-gray-500 hover:text-blue-600 disabled:opacity-30 disabled:hover:text-gray-500 rounded hover:bg-gray-100"
                              title="Move Rank Up"
                            >
                              <ArrowUp className="w-3.5 h-3.5" />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleFormMoveImageDown(index)}
                              disabled={index === formData.images.length - 1}
                              className="p-1 text-gray-500 hover:text-blue-600 disabled:opacity-30 disabled:hover:text-gray-500 rounded hover:bg-gray-100"
                              title="Move Rank Down"
                            >
                              <ArrowDown className="w-3.5 h-3.5" />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleFormRemoveImage(index)}
                              className="p-1 text-red-500 hover:text-red-700 rounded hover:bg-red-50"
                              title="Remove Image"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-xs text-center text-gray-400 py-2 italic bg-gray-50 rounded border border-dashed border-gray-200">
                    No images added yet. Add photos above.
                  </p>
                )}
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1 bg-blue-600 text-white py-2 rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  {isSubmitting ? 'Saving...' : 'Save Changes'}
                </button>
                <button
                  type="button"
                  onClick={resetForm}
                  className="flex-1 bg-gray-100 text-gray-700 py-2 rounded-md hover:bg-gray-200"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Standalone Image Manager & Ranking Studio Modal */}
      <ImageManagerModal
        isOpen={isImageModalOpen}
        onClose={handleCloseImageModal}
        item={imageModalItem}
        type={imageModalType}
        onUpdate={() => {
          fetchProducts();
          fetchServices();
        }}
      />
    </div>
  );
};

export default Inventory;